/**
 * MemberGivingDashboard
 * =====================
 * "My Giving" — a single-page summary of a member's contributions, read from
 * the unified `finance_transactions` ledger via `/api/giving/member-giving/summary`.
 *
 * Rendered:
 *   - 4 KPI tiles  (Lifetime · This Year · Last Year · Count)
 *   - Donut breakdown by category (tithe / offering / building / missions / etc.)
 *   - Recent activity feed (latest 25 entries)
 *   - Optional church_id filter pill so a multi-church member can scope it
 *
 * The endpoint is admin-authed today; this page is mounted under
 * /admin/members/{memberId}/giving so an admin or pastor can review on
 * behalf of a member. The member-self login is the next iteration's job.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, TrendingUp, Calendar, Award, Loader2 } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n, ccy = '') => {
  const v = Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return ccy ? `${ccy} ${v}` : `R${v}`;
};

const CATEGORY_LABELS = {
  tithe: 'Tithes',
  offering: 'Offerings',
  building: 'Building Fund',
  missions: 'Missions',
  thanksgiving: 'Thanksgiving',
  youth: 'Youth',
  welfare: 'Welfare',
  campaign: 'Campaigns',
  donation: 'Donations',
  other: 'Other',
};

const CATEGORY_COLORS = {
  tithe: 'bg-emerald-500',
  offering: 'bg-indigo-500',
  building: 'bg-amber-500',
  missions: 'bg-rose-500',
  thanksgiving: 'bg-fuchsia-500',
  youth: 'bg-orange-500',
  welfare: 'bg-cyan-500',
  campaign: 'bg-violet-500',
  donation: 'bg-slate-500',
  other: 'bg-slate-400',
};

export const MemberGivingDashboard = ({ memberId: propMemberId, churchId, embedded = false }) => {
  const params = useParams();
  const memberId = propMemberId || params.memberId;
  const [yearFilter, setYearFilter] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['member-giving', memberId, churchId, yearFilter],
    queryFn: async () => {
      const q = new URLSearchParams({ member_id: memberId });
      if (churchId) q.append('church_id', churchId);
      if (yearFilter) q.append('year', yearFilter);
      const r = await fetch(`${API_URL}/api/member-giving/summary?${q}`, {
        headers: authHeaders(),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `Request failed (${r.status})`);
      }
      return r.json();
    },
    enabled: !!memberId,
  });

  if (isLoading) {
    return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  }
  if (isError) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-sm text-red-600">{error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const categories = Object.entries(data?.by_category || {});
  const totalByCat = categories.reduce((s, [, v]) => s + (v.total || 0), 0) || 1;

  return (
    <div className={`space-y-5 ${embedded ? '' : 'p-6 max-w-5xl mx-auto'}`} data-testid="member-giving-dashboard">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Giving</h1>
            <p className="text-sm text-slate-500">
              {data?.member_name && (<>For <span className="font-medium text-slate-700">{data.member_name}</span></>)}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-xs text-slate-500">Year:</label>
            <select
              className="text-xs border rounded px-2 py-1"
              value={yearFilter}
              onChange={e => setYearFilter(e.target.value)}
              data-testid="mg-year-filter"
            >
              <option value="">All time</option>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <DollarSign className="h-5 w-5 text-emerald-600 mb-1" />
          <p className="text-[10px] text-slate-500 uppercase">Lifetime</p>
          <p className="text-xl font-bold text-slate-900" data-testid="mg-lifetime">
            {fmt(data?.totals?.lifetime)}
          </p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <TrendingUp className="h-5 w-5 text-indigo-600 mb-1" />
          <p className="text-[10px] text-slate-500 uppercase">This year</p>
          <p className="text-xl font-bold text-slate-900" data-testid="mg-this-year">
            {fmt(data?.totals?.this_year)}
          </p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <Calendar className="h-5 w-5 text-amber-600 mb-1" />
          <p className="text-[10px] text-slate-500 uppercase">Last year</p>
          <p className="text-xl font-bold text-slate-900">{fmt(data?.totals?.last_year)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <Award className="h-5 w-5 text-rose-600 mb-1" />
          <p className="text-[10px] text-slate-500 uppercase">Gifts</p>
          <p className="text-xl font-bold text-slate-900">{data?.totals?.count || 0}</p>
        </CardContent></Card>
      </div>

      {/* By-category breakdown */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-slate-800 mb-3">By category</p>
          {categories.length === 0 ? (
            <p className="text-xs text-slate-400">No giving recorded yet.</p>
          ) : (
            <div className="space-y-2" data-testid="mg-by-category">
              {categories.sort(([, a], [, b]) => b.total - a.total).map(([key, v]) => {
                const pct = ((v.total / totalByCat) * 100).toFixed(1);
                return (
                  <div key={key} className="flex items-center gap-2">
                    <span className="w-24 text-xs text-slate-700">{CATEGORY_LABELS[key] || key}</span>
                    <div className="flex-1 h-3 bg-slate-100 rounded overflow-hidden">
                      <div className={`h-full ${CATEGORY_COLORS[key] || 'bg-slate-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-32 text-right text-xs font-mono text-slate-700">
                      {fmt(v.total)} · {v.count}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent activity */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-slate-800 mb-3">Recent activity</p>
          {!data?.recent?.length ? (
            <p className="text-xs text-slate-400">No recent contributions.</p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="mg-recent">
              {data.recent.map(r => (
                <div key={r.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-800">
                      <Badge variant="outline" className="text-[10px] mr-1 capitalize">{r.category || 'other'}</Badge>
                      {r.memo || r.source || ''}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {r.date && new Date(r.date).toLocaleDateString()} · {r.method || 'unknown'}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-emerald-700 tabular-nums">
                    {fmt(r.amount, r.currency)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MemberGivingDashboard;
